import { useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import {
  AUDIT_ACTION_ICONS,
  AUDIT_ACTION_LABELS,
  AUDIT_STATUT_LABELS,
  AUDIT_STATUT_TONES,
  COMPTE_TYPE_CONFIG,
  type AuditActionKey,
  type AuditEntry,
} from "../data/types";

const ACTION_OPTIONS = [
  { value: "", label: "Toutes les actions" },
  ...(Object.keys(AUDIT_ACTION_LABELS) as AuditActionKey[]).map((key) => ({
    value: key,
    label: AUDIT_ACTION_LABELS[key],
  })),
];

const STATUT_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "reussie", label: "Réussie" },
  { value: "echouee", label: "Échouée" },
];

const TYPE_OPTIONS = [
  { value: "", label: "Tous les types de compte" },
  { value: "proprietaire", label: "Propriétaires" },
  { value: "agence", label: "Agences" },
  { value: "utilisateur", label: "Utilisateurs" },
];

export default function SuperAdminAuditPage() {
  const { audit } = useSuperAdmin();

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const stats = useMemo(
    () => ({
      total: audit.length,
      reussies: audit.filter((entry) => entry.statut === "reussie").length,
      echouees: audit.filter((entry) => entry.statut === "echouee").length,
    }),
    [audit]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return audit.filter((entry) => {
      const matchesSearch =
        !term ||
        [entry.administrateur, entry.action, entry.compteNom, COMPTE_TYPE_CONFIG[entry.compteType].singular]
          .join(" ")
          .toLowerCase()
          .includes(term);
      const matchesAction = !actionFilter || entry.actionKey === actionFilter;
      const matchesStatut = !statutFilter || entry.statut === statutFilter;
      const matchesType = !typeFilter || entry.compteType === typeFilter;
      return matchesSearch && matchesAction && matchesStatut && matchesType;
    });
  }, [audit, search, actionFilter, statutFilter, typeFilter]);

  const isFiltering = Boolean(search.trim() || actionFilter || statutFilter || typeFilter);

  const exportColumns: ExportColumn<AuditEntry>[] = [
    { header: "Administrateur", value: (entry) => entry.administrateur },
    { header: "Action", value: (entry) => entry.action },
    { header: "Compte concerné", value: (entry) => entry.compteNom },
    { header: "Type de compte", value: (entry) => COMPTE_TYPE_CONFIG[entry.compteType].singular },
    { header: "Date et heure", value: (entry) => formatDate(entry.date, true) },
    { header: "Statut de l'action", value: (entry) => AUDIT_STATUT_LABELS[entry.statut] },
  ];

  const resetFilters = () => {
    setSearch("");
    setActionFilter("");
    setStatutFilter("");
    setTypeFilter("");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Journal d'audit</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Chaque action administrative importante est enregistrée ici : administrateur, action, compte concerné, date et
            heure, statut de l'action.
          </p>
        </div>
        <ExportMenu
          rows={filtered}
          columns={exportColumns}
          filename="journal-audit"
          title="Journal d'audit"
          subtitle="Entrées selon les filtres sélectionnés"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon="ri-file-list-3-line" label="Entrées d'audit" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actions réussies" value={String(stats.reussies)} tone="secondary" />
        <StatCard icon="ri-error-warning-line" label="Actions échouées" value={String(stats.echouees)} tone="accent" />
      </div>

      {audit.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="audit-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="audit-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Administrateur, compte, action..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="audit-action" className="text-xs font-label font-medium text-foreground-700">
                Action
              </label>
              <Select
                id="audit-action"
                value={actionFilter}
                onChange={(event) => setActionFilter(event.target.value)}
                options={ACTION_OPTIONS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="audit-type" className="text-xs font-label font-medium text-foreground-700">
                Type de compte
              </label>
              <Select
                id="audit-type"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                options={TYPE_OPTIONS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="audit-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut de l'action
              </label>
              <Select
                id="audit-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={STATUT_OPTIONS}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} entrée{filtered.length > 1 ? "s" : ""} affichée{filtered.length > 1 ? "s" : ""} sur{" "}
              {audit.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {audit.length === 0 && (
        <EmptyState
          icon="ri-shield-check-line"
          title="Aucune entrée d'audit"
          description="Le journal est vide pour l'instant. Dès que vous activerez, suspendrez, désactiverez ou modifierez un compte, l'action sera enregistrée ici."
        />
      )}

      {audit.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucune entrée ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="hidden grid-cols-[1.1fr_1fr_1.3fr_1.3fr_auto] gap-4 border-b border-background-200 bg-background-100/60 px-4 py-3 lg:grid">
            <span className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">
              Administrateur
            </span>
            <span className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">Action</span>
            <span className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">
              Compte concerné
            </span>
            <span className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">
              Date et heure
            </span>
            <span className="text-right text-xs font-label font-semibold uppercase tracking-wide text-foreground-500">
              Statut
            </span>
          </div>

          <ul className="divide-y divide-background-200">
            {filtered.map((entry) => (
              <li
                key={entry.id}
                className="grid grid-cols-1 gap-3 px-4 py-3.5 transition-colors hover:bg-background-100/50 lg:grid-cols-[1.1fr_1fr_1.3fr_1.3fr_auto] lg:items-center lg:gap-4"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-label text-[11px] font-semibold text-secondary-800">
                    {entry.administrateur
                      .split(" ")
                      .slice(0, 2)
                      .map((part) => part.charAt(0))
                      .join("")
                      .toUpperCase()}
                  </span>
                  <span className="truncate text-sm font-label font-medium text-foreground-950">
                    {entry.administrateur}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={cn(AUDIT_ACTION_ICONS[entry.actionKey], "text-sm")} aria-hidden="true" />
                  </span>
                  <span className="text-sm text-foreground-800">{entry.action}</span>
                </div>

                <div className="min-w-0">
                  <p className="truncate text-sm font-label font-medium text-foreground-950">{entry.compteNom}</p>
                  <p className="text-xs text-foreground-500">{COMPTE_TYPE_CONFIG[entry.compteType].singular}</p>
                </div>

                <span className="text-xs text-foreground-600">{formatDate(entry.date, true)}</span>

                <div className="lg:text-right">
                  <Badge tone={AUDIT_STATUT_TONES[entry.statut]}>{AUDIT_STATUT_LABELS[entry.statut]}</Badge>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}